import { useMemo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { getColumnWidth } from '../config/columnWidths';
import type { InternalProjectRecord, ScenarioManagerColumn } from '../types';
import SortableHeader from './SortableHeader';
import { renderCellValue } from './createColumnRenderers';
import type { ColumnRenderersContext } from './createColumnRenderers';
import { createColumnRenderers } from './createColumnRenderers';

type ProjectSort = {
    id: string;
    direction: 'asc' | 'desc';
};

type BuildProjectColumnsParams = ColumnRenderersContext & {
    columnsConfig: ScenarioManagerColumn[];
    projectSort?: ProjectSort;
    onProjectSort: (columnId: string, direction: 'asc' | 'desc') => void;
};

export const useProjectColumns = ({
    columnsConfig,
    projectSort,
    onProjectSort,
    isProjectExpanded,
    showAnalyticsCheckbox,
    onToggleExpansion,
    onScenarioClick,
    getActionMenuItems,
    handleRowAction,
}: BuildProjectColumnsParams): ColumnsType<InternalProjectRecord> => {
    const columnRenderers = useMemo(
        () =>
            createColumnRenderers({
                isProjectExpanded,
                showAnalyticsCheckbox,
                onToggleExpansion,
                onScenarioClick,
                getActionMenuItems,
                handleRowAction,
            }),
        [
            getActionMenuItems,
            handleRowAction,
            isProjectExpanded,
            onScenarioClick,
            onToggleExpansion,
            showAnalyticsCheckbox,
        ],
    );

    return useMemo(
        () =>
            columnsConfig
                .filter((column) => column.is_default)
                .map((column, index) => ({
                    title: (
                        <SortableHeader
                            column={column}
                            projectSort={projectSort}
                            onSort={onProjectSort}
                        />
                    ),
                    key: column.id || `column-${index}`,
                    dataIndex: column.id,
                    width: getColumnWidth(column.id),
                    render: (_value: unknown, record: InternalProjectRecord) =>
                        renderCellValue(column.id, record, columnRenderers),
                })),
        [columnsConfig, columnRenderers, onProjectSort, projectSort],
    );
};
